/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

import listingTypeCommissions from './listingTypeCommissions';

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Sharetribe CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  // B10: Pickup/Delivery search filter — moved from Sharetribe Console
  // listingFields asset (which had key `pickup_delivery`) into local
  // code so the filter key matches what listings actually save under
  // (`publicData.fulfillment`, written by EditListingDetailsForm via
  // form field `pub_fulfillment`). The Console field was deleted; the
  // search schema for key `fulfillment` is set via flex-cli (already
  // documented in docs/SHARETRIBE-CONSOLE-CHECKLIST.md).
  //
  // searchMode 'has_any' = checking either box returns listings that
  // support that option (most renter expectation: "show me kits I
  // can get to me, however"). 'has_all' would only return listings
  // supporting BOTH, which is rarer.
  // Occasions — up to 3 occasions a kit is great for, stored as an
  // ordered array on publicData.occasions. First entry is the
  // "primary" occasion (drives the listing's default display +
  // breadcrumb + categoryLevel1 mirror). Search OR-matches across
  // every entry, so a kit tagged [birthday, holiday] appears under
  // both tiles on the homepage and both filters in the sidebar.
  //
  // searchMode 'has_any' delivers the OR semantics. The flex-cli
  // index was registered via:
  //   npx flex-cli search set --key occasions --type multi-enum
  //     --scope public --schema-for listing -m renttheevent-dev
  //
  // Replaces the legacy single-value `event_type` + `categoryLevel1`
  // role for occasion tagging. categoryLevel1 is still written
  // (mirror = occasions[0]) for backward compat with the breadcrumb
  // + SubThemeFilters reads; can be retired in a follow-up once
  // those move over.
  {
    key: 'occasions',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'birthday', label: 'Birthday' },
      { option: 'bridal', label: 'Bridal & Wedding' },
      { option: 'baby_shower', label: 'Baby Shower' },
      { option: 'holiday', label: 'Holiday' },
      { option: 'milestone', label: 'Milestone' },
      { option: 'outdoor', label: 'Outdoor' },
      { option: 'dinner_parties_gatherings', label: 'Dinner Parties & Gatherings' },
      { option: 'themed_parties', label: 'Themed Parties' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Occasion',
      searchMode: 'has_any',
      group: 'primary',
      searchTier: 'must-have',
    },
    showConfig: {
      label: 'Occasion',
      isDetail: false,
    },
    saveConfig: {
      label: 'Occasion',
      isRequired: true,
    },
  },
  {
    key: 'fulfillment',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'pickup', label: 'Pickup' },
      { option: 'delivery', label: 'Delivery' },
      // Renamed from 'whiteGlove' on 2026-05-13 — see
      // scripts/backfill-whiteglove-to-setup.js for the one-time
      // migration that flipped existing listings. Transaction
      // line-item code 'line-item/white-glove-fee' is intentionally
      // unchanged for back-compat with pre-rename transaction history;
      // see src/util/types.js LINE_ITEM_SETUP_FEE for the constant.
      { option: 'setup', label: 'Setup' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Service options',
      searchMode: 'has_any',
      group: 'primary',
      // Stream 3 PR-4b (2026-05-15): explicit must-have so renters
      // who toggle "Delivery" only see kits that deliver to them.
      // Pickup/delivery is non-negotiable — a kit you can't get to
      // shouldn't appear at all, regardless of how nicely it matches
      // your aesthetic preferences. See SearchPage.shared.js
      // getFilterTier for the two-tier convention.
      searchTier: 'must-have',
    },
    showConfig: {
      label: 'Service options',
      isDetail: false,
    },
    saveConfig: {
      label: 'Service options',
      isRequired: false,
    },
  },
  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }

  // Keywords (a.k.a. Tags) — free-form short-string array stored as
  // publicData.keywords. Not registered as a listing field here on
  // purpose:
  //
  // - The chip input (src/components/ChipInput) is the editor; it
  //   reads/writes publicData.keywords directly via BASICS' submit
  //   handler.
  // - Sharetribe's schemaType enum has no "array of free-form text"
  //   type. The previous registration used multi-enum with empty
  //   enumOptions, which the config helper validates against
  //   validSchemaOptions (configHelpers.js:506) and drops with the
  //   "Unsupported listing extended data configurations detected
  //   (enumOptions)" warning. The drop silently removed the field
  //   from the merged config, but the field still saved to
  //   publicData because BASICS writes it directly — leaving the
  //   wizard's listing-fields-validation consumer out of sync with
  //   what's actually stored.
  // - Listing detail renders keywords from publicData directly
  //   (Stream 3 PR-2's title row). No filter today (Stream 3 PR-4
  //   adds that via Sharetribe Console's keyword index, not via
  //   configListing.js).
  //
  // If a future stream needs a filter chip for tags, register a
  // dedicated tag field via Sharetribe Console (enum with the
  // canonical aesthetic vocabulary, e.g. boho/modern/vintage/...)
  // rather than re-registering this free-form array.

  // Max guest count — the upper bound the kit comfortably serves.
  // Stored as publicData.maxGuestCount (integer).
  //
  // No filterConfig in this PR: a Sharetribe search index for
  // `maxGuestCount` must be registered via flex-cli before searches
  // can filter on pub_maxGuestCount (see docs/SHARETRIBE-CONSOLE-CHECKLIST.md
  // for the `fulfillment` precedent). Stream 3 (search results) adds
  // the filterConfig once the index is in place.
  //
  // No defaultValue — vendors must set this explicitly during listing
  // creation (Stream 1 wizard wires the input + required validation).
  {
    key: 'maxGuestCount',
    scope: 'public',
    schemaType: 'long',
    numberConfig: {
      minimum: 1,
      maximum: 500,
    },
    showConfig: {
      label: 'Seats up to',
      isDetail: true,
    },
    saveConfig: {
      label: 'Maximum guests',
      isRequired: false,
    },
  },

  // Delivery pricing strategy — how the delivery fee is computed when
  // the host offers delivery. Three modes:
  //   flat      — single fixed fee (delivery_fee) regardless of distance
  //                within the host's radius. Current implicit behavior;
  //                new listings write this deliberately.
  //   tiered    — fee scales with distance (tier structure TBD in
  //                Stream 1 wizard work; reserved for future use).
  //   included  — no separate fee, delivery is bundled into base price.
  //
  // defaultValue is a Merrily-internal convention (not a Sharetribe
  // primitive) consumed by the wizard form so new listings save the
  // field deliberately rather than relying on read-side inference.
  {
    key: 'delivery_pricing_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'flat', label: 'Flat fee' },
      { option: 'tiered', label: 'Tiered by distance' },
      { option: 'included', label: 'Included in base price' },
    ],
    defaultValue: 'flat',
    showConfig: {
      label: 'Delivery pricing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Delivery pricing',
      isRequired: false,
    },
  },

  // Setup pricing strategy — how the setup fee is computed when the
  // host offers on-site setup. Four modes:
  //   flat        — single fixed fee (setup_fee). Current implicit
  //                  behavior; new listings write this deliberately.
  //   hourly      — billed per hour of setup labor (rate TBD in
  //                  Stream 1 wizard).
  //   included    — no separate fee, setup is bundled into base price.
  //   quote_only  — setup price is bespoke and negotiated per quote
  //                  (only valid when pricingMode === 'quote').
  //
  // defaultValue: see comment on delivery_pricing_type above.
  {
    key: 'setup_pricing_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'flat', label: 'Flat fee' },
      { option: 'hourly', label: 'Hourly rate' },
      { option: 'included', label: 'Included in base price' },
      { option: 'quote_only', label: 'Quoted per event' },
    ],
    defaultValue: 'flat',
    showConfig: {
      label: 'Setup pricing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Setup pricing',
      isRequired: false,
    },
  },
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - priceVariations      This is relevant only to listings using default-booking process.
 *   - enabled:             If set to true, price variations are enabled.
 *                          Default: false.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 * - transactionFields    You can define an array of custom transaction fields for each listing type. Each transaction field
 *                        should have the following attributes:
 *                        - key (string)
 *                        - label (string)
 *                        - showTo (string, options: 'customer', 'provider'). Option 'provider' is only used for negotiation process.
 *                        - schemaType (string, options: 'enum', 'multi-enum', 'text', 'long', 'boolean', 'youtubeVideoUrl')
 *                        - saveConfig (object, optional,  { required: true })
 *                        - schema specific attributes:
 *                          - numberConfig (object, for schemaType: 'long'): { minimum: number, maximum: number }
 *                          - enumOptions (array, for schemaType: 'enum', 'multi-enum'): [{ label: string, option: string }]
 */

// ⚠ LISTING TYPES ARE MANAGED IN CODE — DO NOT EDIT VIA SHARETRIBE CONSOLE
//
// Each entry below overrides the same-`listingType`-key entry in the hosted
// `/listings/listing-types.json` asset on key collision (see
// `mergeLocalAndHostedListingTypes` + `union` in `src/util/configHelpers.js`).
// Console edits to overridden entries are silently shadowed and do NOT
// reach end users — they're effectively dead config.
//
// Why we override locally instead of editing the hosted asset:
// Sharetribe Console's listing-type editor only exposes the four default
// transaction processes (Calendar booking / Purchase / Free messaging /
// Price negotiation). It can't pick a custom-process alias like
// `event-kit-rental-v2/release-1`. flex-cli has no asset commands.
// SDK asset access is read-only. Editing the hosted asset requires a
// Sharetribe support ticket, and they declined the direct edit (see
// `docs/design/event-kit-rental-process-flip.md`).
//
// To add or modify listing types, edit this file and ship — Console
// edits will be ignored. If a future Sharetribe Console update adds
// custom-process support, revisit and consider migrating back to the
// hosted asset (see TODO.md).
export const listingTypes = [
  {
    // Mirrors the hosted-asset entry field-by-field, except for
    // transactionType.{process,alias} which route to the v2 process so
    // the V2HandoffSection components on the booking detail render.
    listingType: 'event-kit-rental',
    label: 'Event Kit Rental',
    // Commission rates (provider %, customer markup %). Source of truth
    // is src/config/listingTypeCommissions.js — shared with the server
    // resolver (server/api-util/commissionResolver.js).
    commission: listingTypeCommissions['event-kit-rental'],
    transactionType: {
      process: 'event-kit-rental-v2',
      // release-2 (2026-04-27): :at-scheduled self-loop reminder
      // transitions removed; cron schedules them now with idempotency.
      // See docs/design/v2-reminder-self-loop-fix.md.
      alias: 'event-kit-rental-v2/release-2',
      // 'night' (not 'day') because EditListingDetailsPanel.js stamps
      // unitType: 'night' on save for event-kit-rental listings — the
      // wizard's validity check (EditListingDetailsPanel.js:370) compares
      // saved publicData.unitType against this value, and a mismatch
      // renders "Outdated listing!" for every existing listing.
      unitType: 'night',
    },
    availabilityType: 'oneSeat',
    negotiationOptions: {
      customerCounterOffer: true,
      providerUpdateOffer: true,
    },
    transactionFields: [],
    priceVariations: {
      enabled: true,
    },
    defaultListingFields: {
      description: true,
      availability: true,
      payoutDetails: true,
      images: true,
      pickup: false,
      title: true,
      shipping: false,
      location: true,
      price: true,
      stock: false,
    },
  },
  // // Here are some examples of listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  //   availabilityType: 'oneSeat',
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  //   transactionFields: [
  //     {
  //       showTo: 'customer',
  //       label: 'Extra requests for the hosts',
  //       key: 'requests',
  //       schemaType: 'text',
  //     },
  //     {
  //       showTo: 'customer',
  //       label: 'Are you traveling with minors?',
  //       key: 'minors',
  //       schemaType: 'boolean',
  //     },
  //     {
  //       showTo: 'customer',
  //       numberConfig: {
  //         minimum: 1,
  //         maximum: 10,
  //       },
  //       label: 'How many people are staying at the venue',
  //       key: 'peopleStaying',
  //       schemaType: 'long',
  //       saveConfig: {
  //         required: true,
  //       },
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Morning cleanup (10am-12am)',
  //           option: 'morning',
  //         },
  //         {
  //           label: 'Afternoon cleanup (2pm-4pm)',
  //           option: 'afternoon',
  //         },
  //       ],
  //       label: 'Schedule preference',
  //       key: 'schedulePreference',
  //       schemaType: 'enum',
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Vegetarian',
  //           option: 'vegetarian',
  //         },
  //         {
  //           label: 'Vegan',
  //           option: 'vegan',
  //         },
  //         {
  //           label: 'Gluten free',
  //           option: 'glutenFree',
  //         },
  //         {
  //           label: 'No caffeine',
  //           option: 'decaf',
  //         },
  //         {
  //           label: 'Nut free',
  //           option: 'nutFree',
  //         },
  //         {
  //           label: 'Dairy free',
  //           option: 'dairyFree',
  //         },
  //       ],
  //       label: 'Dietary preferences',
  //       key: 'dietaryPreferences',
  //       schemaType: 'multi-enum',
  //     },
  //   ],
  // },
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
